/**
 * The Results tab: packaging a delivery, and sending it.
 *
 * Extracted from ProjectDetailPage on 2026-08-25.
 *
 * **`order` here is an identifier, never a word on screen.** `order_id` is the
 * query parameter the three gated routes take and keeps the backend's name —
 * renaming a route is a contract change for one word — while everything a
 * person reads says *quotation*, which is the piece of agreed work this data
 * belongs to (the facility, 2026-09-16).
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { sessionExpiresAt, sessionLengthHours } from "../auth/AuthContext";
import StorageBrowser from "../components/StorageBrowser";
import DeliveryUploadPanel from "../wizard/DeliveryUploadPanel";
import RecipientPicker from "../components/RecipientPicker";
import MessagePreview from "../components/MessagePreview";
import { G, StatusBadge, Field, Input, Btn, Card, Spinner,
         ProgressBar, Th, formatWhen, formatDay,
         useSortableColumns, useColumnWidths, Sheet, SheetActions } from "../components/Shared";
import { SessionLimitNotice } from "../components/Portal";
import { useDisplayTimezone } from "../portalConfig";
import { fmtBytes, OrderPicker, useTransferOrder } from "../wizard/SubmissionWizard";
import { StorageCell, useVerifier, useRemover, RemoveRecordDialog, VERIFY_ORDER }
  from "./projectRecords";

function NotifyDialog({ project, result, busy, onSend, onClose }) {
  const zone = useDisplayTimezone();
  // Who it actually reached last time is the useful default for "Send again".
  const [recipients, setRecipients] = useState(() =>
    result.notified_recipients?.length ? result.notified_recipients
      : project.representative_email ? [project.representative_email] : []);

  return (
    <Sheet labelledBy="notify-title" onClose={onClose} maxWidth={540} zIndex={200}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <h2 id="notify-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Send delivery notification</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
              {result.file_count} file(s) packaged for {project.name}. Everyone ticked below gets an email.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, color: "#6b7280", minWidth: 32, minHeight: 32 }}>×</button>
        </div>

        {result.notified_recipients?.length > 0 && (
          <div style={{ margin: "14px 0 0", fontSize: 12, color: "#6b7280" }}>
            Last sent to {result.notified_recipients.join(", ")}
            {result.notified_at && ` on ${formatWhen(result.notified_at, { zone })}`}.
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <RecipientPicker projectId={project.id} selected={recipients} onChange={setRecipients} disabled={busy} />
        </div>

        {recipients.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <MessagePreview
              title="What will be sent"
              deps={[project.id, result.id, recipients[0]]}
              fetcher={() => api.get(
                `/api/projects/${project.id}/results/${result.id}/notification-preview?recipient=${encodeURIComponent(recipients[0])}`)}
            />
            {/* The body can name the recipient, so it is not identical for
                everyone — say whose copy this is rather than implying one
                message goes to all of them. */}
            {recipients.length > 1 && (
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                Shown as {recipients[0]} will receive it; the other {recipients.length - 1} get the
                same message addressed to them.
              </div>
            )}
          </div>
        )}

        <SheetActions>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn disabled={busy || recipients.length === 0} onClick={() => onSend(recipients)}>
            {busy ? <><Spinner /> Sending…</>
              : `Send to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`}
          </Btn>
        </SheetActions>
    </Sheet>
  );
}

/**
 * Packaging a delivery — the THIRD gated route, and the one that was left out.
 *
 * `create_result` takes the same optional `order_id` query parameter as
 * submit/start and results/upload/start, and calls the same
 * `_assert_data_transfer_open`. Until 2026-09-16 this form sent no query string
 * at all, so on a project with two or more live quotations "+ Package Results"
 * answered 409 — "…so the data has to name one" — every single time, with
 * nothing on screen to act on. The picker is the same hook the two upload flows
 * use, so there is one rule about which quotation a transfer belongs to rather
 * than three.
 *
 * Exported so a test can drive the form itself: mounting the whole tab would
 * measure the mocks around it more than the request this is about.
 */
export function AddResultForm({ project, onAdded, onCancel }) {
  const [variablePrefix, setVariablePrefix] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);

  const sourcePath = project.results_source_path || "";
  const orderChoice = useTransferOrder(project.id);

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // `orderChoice.query` is the `?order_id=`, empty unless the operator
      // picked one — with a single live quotation the server decides at the
      // moment packaging is queued rather than acting on a list read minutes
      // ago. Either refusal below re-reads that list, so the picker that
      // appears next describes the project as it is now.
      const result = await api.post(
        `/api/projects/${project.id}/results${orderChoice.query}`, {
          variable_prefix: variablePrefix,
        });
      onAdded(result);
    } catch (err) {
      // 404 as well as 409, for the reason the wizard's copy of this gives:
      // 409 is the server asking WHICH quotation, 404 is the one that was named
      // having closed in the meantime, and recovering from only the first left
      // the picker holding a dead choice with no way to refresh it.
      if (err?.status === 409 || err?.status === 404) await orderChoice.reload();
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Package Results</h3>
      <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: "rgba(37,99,235,0.05)", border: "1px solid rgba(37,99,235,0.2)" }}>
        <div style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 600, marginBottom: 3 }}>This project's own S3 bucket</div>
        <div style={{ color: "#4b5563", fontSize: 12 }}>
          Everything under <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{sourcePath}/</strong> is
          packaged into a tar.gz and made available for download. Narrow it to one subfolder below if
          this delivery is only part of what's there.
          {/* Said plainly, because the recipient list used to be on this form:
              without it, removing that section could read as "it now emails
              people silently". */}
          <div style={{ marginTop: 6 }}>
            <strong>Nobody is emailed by this.</strong> Once packaging finishes, use Notify in the table
            below to choose recipients and send.
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Only when there IS a choice — one live quotation needs no click, and
            a project with none is refused by the server in words this form
            cannot improve on. */}
        {orderChoice.mustChoose && <OrderPicker
          orders={orderChoice.live}
          value={orderChoice.chosen}
          onChange={orderChoice.setChosen}
          disabled={busy}
        />}
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <Field label="Subfolder (optional)" hint={`Leave blank to package all of ${sourcePath}/`} style={{ flex: 1 }}>
            <Input
              value={variablePrefix}
              onChange={e => setVariablePrefix(e.target.value)}
              placeholder="results-01"
            />
          </Field>
          <div style={{ display: "flex", gap: 8, marginBottom: 1 }}>
            <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
            <Btn type="submit" disabled={busy || !!orderChoice.blockedBy}>
              {busy ? <><Spinner /> Queueing…</> : "Package & Deliver"}
            </Btn>
          </div>
        </div>
        {/* A dead button with no explanation is the failure this whole panel
            was written about. */}
        {orderChoice.blockedBy && !busy && (
          <div role="status" style={{ fontSize: 12, color: "#6b7280" }}>
            {orderChoice.blockedBy}
          </div>
        )}
      </form>
      {error && (
        <div role="alert" style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
    </Card>
  );
}

function DownloadCell({ projectId, result }) {
  const [loading, setLoading]   = useState(null);  // null | "archive" | "md5"
  const [progress, setProgress] = useState(null);  // {loaded, total} | null
  const [error, setError]       = useState(null);
  const [showWget, setShowWget] = useState(false);

  if (result.status !== "ready") return <span style={{ color: "#6b7280", fontSize: 12 }}>—</span>;

  const archiveName = result.archive_key?.split("/").pop() ?? "results.tar.gz";
  const md5Name     = result.md5_key?.split("/").pop() ?? null;
  const baseUrl     = `/api/projects/${projectId}/results/${result.id}/download`;
  const token       = localStorage.getItem("token");

  const triggerDownload = async (file, filename) => {
    setLoading(file);
    setError(null);
    setProgress(null);
    try {
      if (window.showSaveFilePicker) {
        // Stream the response body straight to a file on disk — the file
        // never has to fit whole in browser memory, so this works the same
        // for a 10 KB checksum file and a multi-terabyte archive.
        const res = await fetch(`${baseUrl}?file=${file}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${res.status}`);
        }
        const total = Number(res.headers.get("Content-Length")) || 0;
        const handle = await window.showSaveFilePicker({ suggestedName: filename });
        const writable = await handle.createWritable();
        const reader = res.body.getReader();
        let loaded = 0;
        setProgress({ loaded: 0, total });
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          await writable.write(value);
          loaded += value.length;
          setProgress({ loaded, total });
        }
        await writable.close();
      } else {
        // Firefox/Safari have no API to stream a fetch() response to disk
        // incrementally from JS. Hand off to a real browser navigation
        // instead: the browser's own download manager streams it to disk
        // and shows its own progress, without ever loading the file into
        // page memory.
        const { url } = await api.get(`/api/projects/${projectId}/results/${result.id}/download-link?file=${file}`);
        window.location.href = url;
      }
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message);
    } finally {
      setLoading(null);
      setProgress(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <Btn
          onClick={() => triggerDownload("archive", archiveName)}
          disabled={loading !== null}
          style={{ fontSize: 12, padding: "5px 12px" }}
        >
          {loading === "archive" ? <><Spinner size={10} /> Downloading…</> : `⬇ ${archiveName}`}
        </Btn>
        {md5Name && (
          <Btn
            variant="secondary"
            onClick={() => triggerDownload("md5", md5Name)}
            disabled={loading !== null}
            style={{ fontSize: 12, padding: "5px 10px" }}
          >
            {loading === "md5" ? <Spinner size={10} /> : "MD5"}
          </Btn>
        )}
        <button
          onClick={() => setShowWget(x => !x)}
          style={{ fontSize: 11, padding: "4px 8px", borderRadius: 5, background: "#f3f4f6", border: "1px solid #e5e7eb", color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}
        >
          wget
        </button>
      </div>
      {progress && progress.total > 0 && (
        <div style={{ minWidth: 180 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#6b7280", marginBottom: 3 }}>
            <span>{fmtBytes(progress.loaded)} / {fmtBytes(progress.total)}</span>
            <span>{Math.round((progress.loaded / progress.total) * 100)}%</span>
          </div>
          <ProgressBar value={(progress.loaded / progress.total) * 100}
                       label={`Downloading ${archiveName}`} />
        </div>
      )}
      {/* role="alert", like the sign-in page's. A download that failed said so
          in red text and to nobody else — and this is the end of the path a
          researcher came here for, so it is the one failure they most need to
          hear. */}
      {error && <div role="alert" style={{ fontSize: 11, color: "#dc2626" }}>{error}</div>}
      {showWget && (
        <pre style={{
          fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
          background: "#1e1e2e", color: "#cdd6f4", borderRadius: 8,
          padding: "10px 14px", overflowX: "auto", margin: 0,
          whiteSpace: "pre-wrap", wordBreak: "break-all",
        }}>
          {`wget -O "${archiveName}" \\\n  --header="Authorization: Bearer ${token}" \\\n  "${window.location.origin}${baseUrl}?file=archive"`}
        </pre>
      )}
    </div>
  );
}

const RESULT_ACCESSORS = {
  created:       r => new Date(r.created_at).getTime(),
  source_prefix: r => r.source_prefix || "",
  files:         r => r.file_count ?? -1,
  status:        r => r.status,
  storage:       r => VERIFY_ORDER.indexOf(r.verified_state ?? null),
  notification:  r => r.notified ? 2 : r.notify_error ? 0 : 1,
};

export default function ResultsTab({ project, canManage, onProjectChanged, sessionHours }) {
  const zone = useDisplayTimezone();
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const [notifying, setNotifying] = useState({});
  const [notifyFor, setNotifyFor] = useState(null);   // result whose recipient dialog is open
  const { sorted: sortedResults, sortKey: resSortKey, sortDir: resSortDir, toggleSort: toggleResSort } = useSortableColumns(results, RESULT_ACCESSORS);
  const { widths: resWidths, setWidth: setResWidth, totalWidth: resTotalWidth } = useColumnWidths({
    created: 170, source_prefix: 240, files: 90, status: 110, download: 200, storage: 190, notification: 190, actions: 220,
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/projects/${project.id}/results`)
      .then(data => { setResults(data); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(load, [load]);

  useEffect(() => {
    const hasProcessing = results.some(r => r.status === "processing" || r.status === "pending");
    if (!hasProcessing) return;
    const id = setTimeout(load, 5000);
    return () => clearTimeout(id);
  }, [results, load]);

  const [verifyBusy, handleVerify] = useVerifier(
    r => `/api/projects/${project.id}/results/${r.id}/verify`, load, setError);

  const remove = useRemover(
    r => `/api/projects/${project.id}/results/${r.id}`, load, setError, onProjectChanged);

  // Sending is deliberately two steps: the button opens the list, and only the
  // dialog's own Send actually mails anyone. Announcing a delivery is visible
  // to real people outside the facility, so it shouldn't happen on one click
  // with no statement of who receives it.
  const handleNotify = async (result, recipients) => {
    setNotifying(n => ({ ...n, [result.id]: true }));
    try {
      await api.post(`/api/projects/${project.id}/results/${result.id}/notify`, { recipients });
      setNotifyFor(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setNotifying(n => ({ ...n, [result.id]: false }));
      load();   // refresh even on failure, so a persisted notify_error shows up immediately
    }
  };

  return (
    <div>
      <SessionLimitNotice hours={sessionHours} sessionHours={sessionLengthHours()} expiresAt={sessionExpiresAt()} zone={zone} kind="download" />
      {notifyFor && (
        <NotifyDialog
          project={project}
          result={notifyFor}
          busy={!!notifying[notifyFor.id]}
          onSend={recipients => handleNotify(notifyFor, recipients)}
          onClose={() => setNotifyFor(null)}
        />
      )}
      {remove.info && (
        <RemoveRecordDialog info={remove.info} busy={remove.busy}
                            onConfirm={remove.confirm} onClose={remove.close} />
      )}
      {canManage && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center",
                      marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
          {/* The server already decided whether this project may take data —
              an unsigned quotation or an unconfirmed blocking document closes
              it — and says why. Offering the buttons anyway and letting the
              409 explain afterwards is what made the Results tab look broken. */}
          {project.can_transfer_data === false && (
            <span role="status" style={{ fontSize: 12, color: "#92400e", marginRight: "auto" }}>
              {project.transfer_blocked_reason || "This project cannot take data yet."}
            </span>
          )}
          {!uploading && (
            <Btn variant="secondary" disabled={project.can_transfer_data === false}
                 onClick={() => setUploading(true)}>Upload files</Btn>
          )}
          {!showForm && (
            <Btn disabled={project.can_transfer_data === false}
                 onClick={() => setShowForm(true)}>+ Package Results</Btn>
          )}
        </div>
      )}

      {/* Getting data IN used to need an external S3 client — that is, exactly
          the credentials an operator no longer has now that storage belongs to
          the group. */}
      {canManage && uploading && (
        <DeliveryUploadPanel project={project}
                             onDone={() => { setUploading(false); load(); onProjectChanged?.(); }} />
      )}

      {/* Everyone with access sees this, confined server-side: an operator to
          the whole project folder, a researcher to the delivery area. Before it
          there was no way to fetch a delivered file except a link somebody
          sent. */}
      <StorageBrowser project={project} />

      {/* onProjectChanged as well as load(): result_count lives on the project,
          and only deletion refreshed it — so the number went down on delete and
          never up on create until a full page reload. */}
      {showForm && (
        <AddResultForm project={project}
                       onAdded={() => { setShowForm(false); load(); onProjectChanged?.(); }}
                       onCancel={() => setShowForm(false)} />
      )}

      {error && (
        <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {loading && results.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#6b7280" }}>Loading…</div>
      ) : results.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No results yet</div>
          {canManage
            ? <div style={{ fontSize: 13 }}>Use "Package Results" to archive and deliver results to this project.</div>
            : <div style={{ fontSize: 13 }}>Results will appear here once the facility packages them.</div>}
        </div>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: resTotalWidth - (canManage ? 0 : resWidths.actions) }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <Th label="Created" sortKey="created" activeKey={resSortKey} dir={resSortDir} onSort={toggleResSort} width={resWidths.created} onWidth={w => setResWidth("created", w)} />
                  <Th label="Source Prefix" sortKey="source_prefix" activeKey={resSortKey} dir={resSortDir} onSort={toggleResSort} width={resWidths.source_prefix} onWidth={w => setResWidth("source_prefix", w)} />
                  <Th label="Files" sortKey="files" activeKey={resSortKey} dir={resSortDir} onSort={toggleResSort} width={resWidths.files} onWidth={w => setResWidth("files", w)} />
                  <Th label="Status" sortKey="status" activeKey={resSortKey} dir={resSortDir} onSort={toggleResSort} width={resWidths.status} onWidth={w => setResWidth("status", w)} />
                  <Th label="Download" width={resWidths.download} onWidth={w => setResWidth("download", w)} />
                  <Th label="Storage" sortKey="storage" activeKey={resSortKey} dir={resSortDir} onSort={toggleResSort} width={resWidths.storage} onWidth={w => setResWidth("storage", w)} />
                  <Th label="Notification" sortKey="notification" activeKey={resSortKey} dir={resSortDir} onSort={toggleResSort} width={resWidths.notification} onWidth={w => setResWidth("notification", w)} />
                  {canManage && <Th label="" width={resWidths.actions} onWidth={w => setResWidth("actions", w)} />}
                </tr>
              </thead>
              <tbody>
                {sortedResults.map(r => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td data-label="Created" style={{ padding: "12px 16px", fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
                    {formatWhen(r.created_at, { zone })}
                  </td>
                  <td data-label="Source Prefix" style={{ padding: "12px 16px" }}>
                    {r.source_prefix
                      ? <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "#6b7280" }}>{r.source_prefix}</span>
                      : <span style={{ color: "#6b7280", fontSize: 12 }}>—</span>}
                  </td>
                  <td data-label="Files" style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                    {r.file_count != null ? r.file_count : (r.status === "processing" ? <Spinner size={12} /> : "—")}
                  </td>
                  <td data-label="Status" style={{ padding: "12px 16px" }}>
                    <StatusBadge status={r.status} />
                    {/* Cancelled carries a message too, and it is the reassuring
                        one — that nothing was deleted and the source is intact. */}
                    {(r.status === "failed" || r.status === "cancelled") && r.error_msg && (
                      <div style={{ fontSize: 11, color: r.status === "failed" ? "#dc2626" : "#6b7280",
                                    marginTop: 4, maxWidth: 200 }} title={r.error_msg}>
                        {r.error_msg.slice(0, 60)}{r.error_msg.length > 60 ? "…" : ""}
                      </div>
                    )}
                  </td>
                  <td data-label="Download" style={{ padding: "12px 16px" }}>
                    <DownloadCell projectId={project.id} result={r} />
                  </td>
                  <td data-label="Storage" style={{ padding: "12px 16px" }}>
                    <StorageCell row={r} canManage={canManage} busy={!!verifyBusy[r.id]} onVerify={handleVerify} />
                  </td>
                  <td data-label="Notification" style={{ padding: "12px 16px" }}>
                    {r.notified
                      ? <div>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>Notified {formatDay(r.notified_at, zone)}</span>
                          {/* Who was actually reached, not just that something
                              was sent — with members included that is several
                              addresses and worth being able to check. */}
                          {r.notified_recipients?.length > 0 && (
                            <div style={{ fontSize: 11, color: "#6b7280" }} title={r.notified_recipients.join(", ")}>
                              {r.notified_recipients.length === 1
                                ? r.notified_recipients[0]
                                : `${r.notified_recipients.length} recipients`}
                            </div>
                          )}
                          {r.notify_error && (
                            <div style={{ fontSize: 11, color: "#b45309" }} title={r.notify_error}>
                              Some failed: {r.notify_error.slice(0, 40)}…
                            </div>
                          )}
                        </div>
                      : r.notify_error
                        ? <div style={{ fontSize: 11, color: "#b91c1c" }} title={r.notify_error}>
                            Failed: {r.notify_error.slice(0, 50)}{r.notify_error.length > 50 ? "…" : ""}
                          </div>
                        : <span style={{ fontSize: 12, color: "#6b7280" }}>Not sent</span>}
                  </td>
                  {canManage && (
                    <td data-label="" style={{ padding: "12px 16px", textAlign: "right" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
                        {r.status === "ready" && !r.notified && (
                          <Btn
                            variant="ghost"
                            disabled={notifying[r.id]}
                            onClick={() => setNotifyFor(r)}
                            style={{ fontSize: 12, padding: "5px 12px" }}
                          >
                            {r.notify_error ? "↻ Retry…" : "🔔 Notify…"}
                          </Btn>
                        )}
                        {r.status === "ready" && r.notified && (
                          <>
                            <span style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ color: G }}>✓</span> Notified
                            </span>
                            {/* A delivery often needs telling someone who was
                                missed the first time — without this, an already
                                notified result had no way to reach anyone else. */}
                            <Btn variant="secondary" onClick={() => setNotifyFor(r)}
                              style={{ fontSize: 12, padding: "4px 10px" }}>
                              Send again…
                            </Btn>
                          </>
                        )}
                        {/* Rendered for every status, not just "ready". A
                            packaging run that failed left a row that counted
                            towards the project's storage health and had no
                            control of any kind in this cell. */}
                        <Btn variant="ghost" onClick={() => remove.open(r)}
                             style={{ fontSize: 12, padding: "5px 10px", color: "#b91c1c" }}>
                          Remove…
                        </Btn>
                      </div>
                    </td>
                  )}
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

